import React, { useState } from 'react';
import { Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import JudoClassificationPicker from './JudoClassificationPicker';
import SelectWithOther from './SelectWithOther';
import { JUDO_MASTERY_LEVELS, formatJudoClassification } from '../../data/combativesJudo';

/**
 * JudoTechniqueRow — one technique inside a Judo session. Mirrors
 * BJJ's TechniqueRow ergonomics (collapsed-summary / expanded-detail
 * pattern) but the picker is mechanism-based (group → subgroup →
 * named technique), not position-based.
 */
const JudoTechniqueRow = ({ value, onChange, onRemove, index, nameSuggestions = [] }) => {
  const [expanded, setExpanded] = useState(true);
  const set = (patch) => onChange({ ...value, ...patch });

  const summary = formatJudoClassification(value.classification) || 'New technique';
  const datalistId = `judo-technique-suggestions-${index}`;

  return (
    <div className="bg-enf-surface-mid border border-enf-surface-border rounded-md" data-testid={`judo-technique-row-${index}`}>
      {/* Collapsed header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
        data-testid={`judo-technique-toggle-${index}`}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-enf-text-muted flex-shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-enf-text-muted flex-shrink-0" />}
          <span className="text-sm text-enf-text-secondary truncate">{summary}</span>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-enf-text-muted flex-shrink-0">{value.mastery}</span>
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-enf-surface-border">
          {/* Classification cascading picker */}
          <div className="pt-3">
            <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Classification</label>
            <JudoClassificationPicker
              value={value.classification}
              onChange={(cls) => set({ classification: cls })}
              testId={`judo-technique-${index}-cls`}
            />
          </div>

          {/* Outcome */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Outcome</label>
            <SelectWithOther
              value={value.outcome}
              onChange={(outcome) => set({ outcome })}
              suggestions={['Drilled', 'Landed in randori', 'Countered', 'Failed entry', 'Reset / no throw']}
              placeholder="Select outcome…"
              testId={`judo-technique-${index}-outcome`}
            />
          </div>

          {/* Reps + Partner level */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Reps</label>
              <input
                type="number" min={0} max={999}
                value={value.reps}
                onChange={(e) => set({ reps: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
                data-testid={`judo-technique-${index}-reps`}
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Partner level (optional)</label>
              <select
                value={value.partnerLevel}
                onChange={(e) => set({ partnerLevel: e.target.value })}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
                data-testid={`judo-technique-${index}-partner-level`}
              >
                <option value="">—</option>
                <option value="Junior">Junior</option>
                <option value="Peer">Peer</option>
                <option value="Senior">Senior</option>
                <option value="Sensei">Sensei</option>
              </select>
            </div>
          </div>

          {/* Mastery */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Mastery</label>
            <div className="flex gap-1.5">
              {JUDO_MASTERY_LEVELS.map((m) => {
                const active = value.mastery === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => set({ mastery: m.value })}
                    aria-pressed={active}
                    data-testid={`judo-technique-${index}-mastery-${m.value.toLowerCase()}`}
                    className={`flex-1 px-2 py-1.5 text-xs rounded-md border font-semibold transition-colors ${
                      active
                        ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary'
                        : 'bg-enf-surface-deep border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                    }`}
                  >
                    {m.value}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-enf-text-muted leading-snug mt-1">
              {JUDO_MASTERY_LEVELS.find((m) => m.value === value.mastery)?.help}
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Notes</label>
            <textarea
              value={value.notes}
              onChange={(e) => set({ notes: e.target.value })}
              placeholder="Cues, partner feedback, what didn't work…"
              rows={2}
              className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-y"
              data-testid={`judo-technique-${index}-notes`}
            />
          </div>

          {/* Remove */}
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-enf-role-warning hover:text-enf-text-primary flex items-center gap-1.5"
            data-testid={`judo-technique-${index}-remove`}
          >
            <Trash2 className="w-3 h-3" /> Remove technique
          </button>
        </div>
      )}
    </div>
  );
};

export default JudoTechniqueRow;
